import React, { useEffect, useRef } from 'react'
import { Box, Container, styled, Tooltip, tooltipClasses, Typography, useMediaQuery } from '@mui/material'
import partnerLogos from './partnerLogos'
import SafeScrollLink from './SafeScrollLink';
import { motion } from 'framer-motion';
import { ReactComponent as Partners } from "./partners_icon.svg";
import { useNavigate } from 'react-router-dom';

const PartnersSvg = () => {
    const editedIcon = React.cloneElement(<Partners />, {
        style: {
            height: "40px",
            width: "40px",
            minHeight: "40px",
            minWidth: "40px",
            filter: "drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.4))",
        },
    });
    return <>{editedIcon}</>;
};

function Motion(props) {
    const { scale, stiffness, damping, dontHover } = props;

    const clonedComp = React.cloneElement(props.children, {
        component: motion.div,
        whileHover: { scale: scale || (dontHover ? 1 : 1.08) },
        transition: {
            type: "spring",
            stiffness: stiffness || 150,
            damping: damping || 10,
        },
    });

    return <>{clonedComp}</>;
}

const cenikBlueDark = "#001949";
const baseBorder = "1px solid #D9D9D9";

const HtmlTooltip = styled(
    ({ className, customStyle = {}, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ),
    {
        shouldForwardProp: (prop) => prop !== "customStyle", // Don't pass this to the DOM
    }
)(({ theme, customStyle }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: cenikBlueDark,
        color: "rgba(0, 0, 0, 0.87)",
        maxWidth: 230,
        border: baseBorder,
        borderRadius: "8px",
        margin: "5px",
        ...customStyle, // ✅ customStyle is available here
    },
}));

export default function PartnersSlider({ sxContainer = {}, sxBox = {}, hideNavigate = false, tooltipPlacement = "top" }) {
    const phone = useMediaQuery("(max-width:599.95px)");
    const tablet = useMediaQuery("(min-width:600px) and (max-width:899.95px)");
    const ref = useRef(null);
    const containerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            if (!ref.current || !containerRef.current) return;

            const rect = ref.current.getBoundingClientRect();
            const scrollOffset = Math.max(0, window.innerHeight - rect.top);
            const MAX_SCROLL_DOWN = 1200;
            const SPEED = 3;
            const margin = Math.min(MAX_SCROLL_DOWN, scrollOffset) / SPEED;

            containerRef.current.childNodes.forEach((row, i) => {
                if (!row) return;
                if (i % 2 === 0) {
                    row.style.paddingLeft = `${margin}px`;
                    row.style.paddingRight = '';
                } else {
                    row.style.paddingRight = `${margin}px`;
                    row.style.paddingLeft = '';
                }
            });
        };

        window.addEventListener("scroll", handleScroll);
        requestAnimationFrame(handleScroll);

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const MIN_LOGOS_PER_LINE = phone ? 3 : 4;
    const chunkSizes = tablet ? [5, 5, 5] : phone ? [4, 4, 4, 3] : [7, 8];

    const chunkedLogos = chunkLogos(chunkSizes);
    const minLineHeight = 66;
    const paddingY = 30;


    function chunkLogos(sizeOrArray = 7) {
        const defaultSize = typeof sizeOrArray === "number" ? sizeOrArray : sizeOrArray[0];
        const chunks = [];
        let startIndex = 0;
        let rowIndex = 0;

        while (startIndex < partnerLogos.length) {
            const rowSize =
                Array.isArray(sizeOrArray) && sizeOrArray[rowIndex] != null
                    ? sizeOrArray[rowIndex]
                    : defaultSize;

            const chunk = partnerLogos.slice(startIndex, startIndex + rowSize);
            chunks.push(chunk);
            startIndex += rowSize;
            rowIndex++;
        }

        return chunks
            .map((chunk, i) => {
                const len = chunk.length;
                if (len < MIN_LOGOS_PER_LINE) return chunk;
                const first = chunk[0];
                const sec = chunk[1];
                const last = chunk[len - 1];
                const prelast = chunk[len - 2];
                return [prelast, last, ...chunk, first, sec];
            })
            .filter(c => c.length >= MIN_LOGOS_PER_LINE);
    }

    return (
        <div ref={ref}>
            <Container maxWidth="lg" sx={{ px: "0px !important", ...sxContainer }}>


                <Box sx={{ position: 'relative', px: { xs: "16px", sm: "24px", md: "24px", lg: "24px" } }}>

                    {!hideNavigate && <HtmlTooltip
                        customStyle={{ maxWidth: '300px' }}
                        enterTouchDelay={100}
                        enterDelay={400}
                        enterNextDelay={300}
                        leaveDelay={0}
                        title={
                            <React.Fragment>
                                <div
                                    style={{
                                        padding: "10px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontSize: "Roboto",
                                            fontWeight: 400,
                                            color: "white",
                                        }}
                                    >
                                        <><b>Podmínky partnerské spolupráce</b></>
                                    </Typography>
                                </div>
                            </React.Fragment>
                        }
                        placement={"top"}>
                        <Box sx={{
                            position: 'absolute',
                            top: -20,
                            right: 14,
                            zIndex: "101",
                            cursor: 'pointer'
                        }} onClick={() => {
                            navigate("/partner_form");
                        }}>
                            <Motion>
                                <Box>
                                    <PartnersSvg />
                                </Box>
                            </Motion>
                        </Box>
                    </HtmlTooltip>}

                    <Box
                        ref={containerRef}
                        sx={{
                            zIndex: 3,
                            position: 'relative',
                            pt: `${paddingY + 5}px`,
                            pb: `${paddingY}px`,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            alignContent: "center",
                            mx: '-80px',
                            WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                            maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                            ...sxBox,
                        }}
                    >
                        <Box sx={{ position: 'absolute', top: 0, bgcolor: "#F3F3F3", height: `100%`, width: '100vw' }} />
                        {chunkedLogos.map((logos, i) =>
                            <Box
                                key={"ChunkMain" + i}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    alignContent: "center",
                                    gap: "32px",

                                    WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                                    maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                                    pb: i === chunkedLogos.length - 1 ? '0px' : '24px',
                                }}
                            >
                                {logos.map((logo, y) =>
                                    <Motion scale={logo.link ? 1.03 : 1} key={"PartMotion" + y}>
                                        <Box>
                                            <SafeScrollLink key={"ChunkLogo" + y} target='_blank' to={logo.link || undefined} style={{ textDecoration: "none", cursor: logo.link ? 'pointer' : 'default' }} onClick={(e) => {
                                                if (!logo.link) {
                                                    e.preventDefault();
                                                }
                                            }}>
                                                <HtmlTooltip
                                                    key={"ToolTPartners" + y}
                                                    customStyle={{ maxWidth: '300px' }}
                                                    id={"HtmlTooltipCenik" + y}
                                                    enterTouchDelay={100}
                                                    enterDelay={400}
                                                    enterNextDelay={300}
                                                    leaveDelay={0}
                                                    title={
                                                        <React.Fragment>
                                                            <div
                                                                style={{
                                                                    padding: "10px",
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "13px",
                                                                        fontSize: "Roboto",
                                                                        fontWeight: 400,
                                                                        color: "white",
                                                                    }}
                                                                >
                                                                    <><b>Partner: {logo.name}</b><br />Obor činnosti: {logo.field}</>
                                                                </Typography>
                                                            </div>
                                                        </React.Fragment>
                                                    }
                                                    placement={tooltipPlacement}
                                                >

                                                    <Box
                                                        component="img"
                                                        src={logo.src}
                                                        sx={{ width: "auto", maxWidth: "140px", maxHeight: '40px', userSelect: "none" }}
                                                        alt="Partners"
                                                    />

                                                </HtmlTooltip>
                                            </SafeScrollLink>
                                        </Box>
                                    </Motion>
                                )}

                            </Box>
                        )}
                    </Box>
                </Box>
            </Container>
        </div>
    )
}
